import React, { useEffect, useState, useCallback } from 'react';
import api from '../api-instance';
import { useNavigate, Link } from 'react-router-dom';
import NavBar from './NavBar';
import { Toaster } from 'react-hot-toast';

// Helper function to format large numbers
const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return String(num);
};

// Helper function to format percentage
const formatPercentage = (num) => {
  return num.toFixed(1) + '%';
};

const Stats = () => {

  const [banners, setBanners] = useState([]);
  const [bannerList, setBannerList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingExpirations, setUpcomingExpirations] = useState([]);
  const [kpiMetrics, setKpiMetrics] = useState({
    totalBanners: 0,
    activeCampaigns: 0,
    totalImpressions: '0',
    avgCTR: '0%',
    allUsers: 0
  });

  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await api.get('/banners');
      const banners = response.data || [];
      setBanners(banners);
      setBannerList(banners);

      const activeCount = banners.filter(banner => banner.status === 'live').length;
      // Fetch user count
      let userCount = 1;
      try {
        const usersResponse = await api.get('/users');
        userCount = usersResponse.data?.length || 1;
      } catch (err) {
        console.error('Failed to fetch users count', err);
      }

      // Calculate KPI metrics
      const totalBanners = banners.length;
      const activeCampaigns = activeCount; // Active banners = active campaigns
      
      // For now, using placeholder values for impressions and CTR
      // TODO: Fetch from metrics API when available
      // Try to fetch metrics if endpoint exists
      let totalImpressions = '0';
      let avgCTR = '0%';
      try {
        // Attempt to fetch metrics statistics
        // This will fail gracefully if endpoint doesn't exist
        const metricsResponse = await api.get('/metrics/stats').catch(() => null);
        if (metricsResponse?.data) {
          const impressions = metricsResponse.data.totalImpressions || 0;
          const ctr = metricsResponse.data.avgCTR || 0;
          totalImpressions = typeof impressions === 'number' ? formatNumber(impressions) : impressions;
          avgCTR = typeof ctr === 'number' ? formatPercentage(ctr) : ctr;
        } else {
          // Use placeholder values
          totalImpressions = '0M';
          avgCTR = '0%';
        }
      } catch (err) {
        // Use placeholder values if metrics endpoint doesn't exist
        totalImpressions = '0M';
        avgCTR = '0%';
      }

      setKpiMetrics({
        totalBanners,
        activeCampaigns,
        totalImpressions,
        avgCTR,
        allUsers: userCount
      });

      // Calculate recent activity (last 3 banners sorted by updatedAt)
      const recentBanners = [...banners]
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        .slice(0, 3)
        .map(banner => {
          const target = banner.target || {};
          const contentIds = Array.isArray(target.contentIds) ? target.contentIds : [];
          const storyId = contentIds.length > 0 ? contentIds[0] : null;
          return {
            id: banner.id,
            name: banner.name,
            action: banner.status === 'paused' ? 'paused' : 'edited',
            updatedAt: banner.updatedAt,
            storyId: storyId
          };
        });
      setRecentActivity(recentBanners);

      // Calculate upcoming expirations (banners with endAt in the next 7 days)
      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const expiringBanners = banners
        .filter(banner => {
          if (!banner.endAt) return false;
          const endDate = new Date(banner.endAt);
          return endDate >= now && endDate <= sevenDaysFromNow;
        })
        .sort((a, b) => new Date(a.endAt) - new Date(b.endAt))
        .slice(0, 3)
        .map(banner => {
          const endDate = new Date(banner.endAt);
          const diffTime = endDate - now;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          const target = banner.target || {};
          const contentIds = Array.isArray(target.contentIds) ? target.contentIds : [];
          const storyId = contentIds.length > 0 ? contentIds[0] : null;
          return {
            id: banner.id,
            name: banner.name,
            daysLeft: diffDays,
            endAt: banner.endAt,
            storyId: storyId
          };
        });
      setUpcomingExpirations(expiringBanners);
      
    } catch (err) {
      console.error('Failed to fetch banners', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  
  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-slate-600 text-sm font-medium">Loading...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      
      <NavBar />
      
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Stats */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-slate-900">Overview</h3>
              <p className="text-sm text-slate-500 mt-1">Monitor your banner performance and activity</p>
            </div>
          </div>
          
          {/* KPI Metrics */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-8">
            <div className="group relative bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl border border-blue-200/50 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden px-6 py-5">
              <div className="absolute top-0 right-0 w-20 h-20 bg-blue-200/30 rounded-full -mr-10 -mt-10 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <dt className="text-sm font-medium text-slate-600 truncate">Total Banners</dt>
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <dd className="text-3xl font-bold tracking-tight text-slate-900">{kpiMetrics.totalBanners}</dd>
              </div>
            </div>
            <div className="group relative bg-gradient-to-br from-green-50 to-green-100/50 rounded-2xl border border-green-200/50 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden px-6 py-5">
              <div className="absolute top-0 right-0 w-20 h-20 bg-green-200/30 rounded-full -mr-10 -mt-10 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <dt className="text-sm font-medium text-slate-600 truncate">Active Campaigns</dt>
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <dd className="text-3xl font-bold tracking-tight text-slate-900">{kpiMetrics.activeCampaigns}</dd>
              </div>
            </div>
            <div className="group relative bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-2xl border border-purple-200/50 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden px-6 py-5">
              <div className="absolute top-0 right-0 w-20 h-20 bg-purple-200/30 rounded-full -mr-10 -mt-10 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <dt className="text-sm font-medium text-slate-600 truncate">Total Impress.</dt>
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                </div>
                <dd className="text-3xl font-bold tracking-tight text-slate-900">{kpiMetrics.totalImpressions}</dd>
              </div>
            </div>
            <div className="group relative bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-2xl border border-orange-200/50 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden px-6 py-5">
              <div className="absolute top-0 right-0 w-20 h-20 bg-orange-200/30 rounded-full -mr-10 -mt-10 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <dt className="text-sm font-medium text-slate-600 truncate">Avg. CTR</dt>
                  <div className="p-2 bg-orange-500/10 rounded-lg">
                    <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                </div>
                <dd className="text-3xl font-bold tracking-tight text-slate-900">{kpiMetrics.avgCTR}</dd>
              </div>
            </div>
            <div className="group relative bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-2xl border border-indigo-200/50 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden px-6 py-5">
              <div className="absolute top-0 right-0 w-20 h-20 bg-indigo-200/30 rounded-full -mr-10 -mt-10 blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <dt className="text-sm font-medium text-slate-600 truncate">All Users</dt>
                  <div className="p-2 bg-indigo-500/10 rounded-lg">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                </div>
                <dd className="text-3xl font-bold tracking-tight text-slate-900">{kpiMetrics.allUsers}</dd>
              </div>
            </div>
          </div>

          {/* Recent Activity and Upcoming Expirations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Activity Panel */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-900">Recent Activity</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Latest banner updates</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  {recentActivity.length > 0 ? (
                    recentActivity.map((activity, index) => (
                      <li key={activity.id || index} className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200">
                        <div className="mt-0.5 flex-shrink-0">
                          <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-900">
                            <span className="font-semibold">{activity.name}</span>
                            {activity.storyId && (
                              <span className="text-slate-500 ml-1.5 text-xs">(Story ID: {activity.storyId})</span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5 capitalize">{activity.action}</p>
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="text-sm text-slate-400 text-center py-8">No recent activity</li>
                  )}
                </ul>
              </div>
            </div>

            {/* Upcoming Expirations Panel */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {upcomingExpirations.length > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-sm">
                        {upcomingExpirations.length}
                      </span>
                    )}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-900">Upcoming Expirations</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Expiring in the next 7 days</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  {upcomingExpirations.length > 0 ? (
                    upcomingExpirations.map((expiration, index) => (
                      <li key={expiration.id || index} className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200">
                        <div className="mt-0.5 flex-shrink-0">
                          <div className={`w-2 h-2 rounded-full ${expiration.daysLeft <= 1 ? 'bg-red-500' : expiration.daysLeft <= 3 ? 'bg-orange-500' : 'bg-yellow-500'}`}></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-900">
                            <span className="font-semibold">{expiration.name}</span>
                            {expiration.storyId && (
                              <span className="text-slate-500 ml-1.5 text-xs">(Story ID: {expiration.storyId})</span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {expiration.daysLeft === 0 ? (
                              <span className="text-red-600 font-medium">Expires today</span>
                            ) : (
                              <span>Ends in <span className="font-medium">{expiration.daysLeft} day{expiration.daysLeft !== 1 ? 's' : ''}</span></span>
                            )}
                          </p>
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="text-sm text-slate-400 text-center py-8">No upcoming expirations</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Toaster />
    </div>
  );
};

export default Stats;
